import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'

import { supportedLanguages } from '~/data/lang/settings'
import { sanityClientCdn } from '~/services/sanity'
import type { ArticlesPageData } from '~/utils/articles/getArticlesPageProps'
import { getArticlesPageProps } from '~/utils/articles/getArticlesPageProps'
import type { IPaginationContext } from '~/utils/doPagination'
import { addTranslations } from '~/utils/i18n'
import type { PageType } from '~/utils/urlResolver'
import { urlQuery } from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import ArticlesPage from './page/[page]'

export const ArticlesIndexPage: NextPage<ArticlesPageData> = (
  props: ArticlesPageData
) => {
  return <ArticlesPage {...props} />
}

const getStaticPropsOriginal: GetStaticProps = async function getStaticProps(
  context
) {
  try {
    // Get SEO templates from centralized _app.tsx loading
    const seoTemplates = (context as any)?.pageProps?.seoTemplates
    const pageProps = await getArticlesPageProps(
      context as IPaginationContext,
      seoTemplates
    )

    if (!pageProps) {
      console.log('No page props returned')
      return {
        notFound: true,
      }
    }

    // Transform articles and featured articles to use translations when available
    if (pageProps.articles) {
      pageProps.articles = pageProps.articles.map(article => {
        if (article.translatedVersion) {
          return {
            ...article.translatedVersion,
            originalVersion: article,
          }
        }
        return article
      })
    }

    if (pageProps.featuredArticles) {
      pageProps.featuredArticles = pageProps.featuredArticles.map(
        (article: any) => {
          if (article.translatedVersion) {
            return {
              ...article.translatedVersion,
              originalVersion: article,
            }
          }
          return article
        }
      )
    }

    return {
      props: {
        ...pageProps,
        notFound: false,
      },
    }
  } catch (error) {
    console.error('--- Error in getStaticProps:', error)
    return {
      notFound: true,
    }
  }
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(async context => {
  const result = await getStaticPropsOriginal(context)

  if ('props' in result) {
    return {
      props: await addTranslations(result.props, context),
    }
  }

  return result
})

export const getStaticPaths: GetStaticPaths = async () => {
  const limiter =
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF === 'main'
      ? '[0..10]'
      : '[0..10]'

  const pages = await sanityClientCdn.fetch<Array<{ url: PageType }>>(
    `*[(_type in $pageTypes && _type != 'artist' && defined(slug.current) && (!defined(isVisible) || isVisible == true) && !(_id in path('drafts.**')))]
    | score(
      boost(_type match "article", 3))
    | order(_score desc)
      ${limiter} {
      ${urlQuery}
    }`,
    {
      pageTypes: ['article'],
    }
  )

  const pagesToBuild = pages

  const paths = (pagesToBuild?.length ? pagesToBuild : [])
    .filter(p => !['/', '404', 'artworks', 'search'].includes(p.url.slug))
    .flatMap(p => {
      // Handle base configuration for article paths
      const getBasePath = (lang: string) => {
        // If no parent, it's a general article
        if (!p.url.parent) {
          return {
            params: {
              lang,
              category: 'all',
              articleTaxonomy: p.url.articleTaxonomy || 'articles',
              slug: p.url.slug,
            },
          }
        }

        // If parent is an artist or category
        return {
          params: {
            lang,
            category: p.url.parent.slug,
            articleTaxonomy: p.url.articleTaxonomy || 'articles',
            slug: p.url.slug,
          },
        }
      }

      const basePath = getBasePath('en')

      const languagePaths = supportedLanguages.map(lang => getBasePath(lang))

      // Filter out any invalid paths and return combined array
      return [basePath, ...languagePaths].filter(
        path =>
          path.params.slug &&
          path.params.articleTaxonomy &&
          path.params.category
      )
    })

  return {
    paths: paths,
    fallback: 'blocking',
  }
}

export default ArticlesIndexPage
